import { Injectable } from "@angular/core";
import { User } from "../models/auth.models";

// Permissões que só leem: em "Ver como" são as únicas que contam.
const READ_ONLY_PERMISSION_PREFIXES = ["VIEW_", "CONSULT_", "DOWNLOAD_"];

@Injectable({
  providedIn: "root",
})
export class PermissionsService {
  private permissions: Set<string | undefined> = new Set<string>();
  mastersRoles = ["MASTER", "STORE OPERATOR"];
  agentRoles = ["AGENT", "COMMERCIAL DIRECTOR"];
  testPlans = ["Free_Trial"];
  noAdminRoles = this.mastersRoles.concat(this.agentRoles);
  userAuth!: User;

  constructor() {}

  loadPermissions(permissions: (string | undefined)[] | undefined): void {
    if (permissions && permissions?.length > 0) {
      this.permissions = new Set(permissions);
    } else {
      const p = JSON.parse(localStorage.getItem("currentUser") || "{}");
      if (p && p?.usersPermissionsForEntities?.length > 0) {
        this.permissions = new Set(
          p.usersPermissionsForEntities.map(
            (p: any) => p.permissionForEntity?.name,
          ),
        );
      }
    }
  }

  /**
   * "Ver como" só de leitura: o MASTER está a ver o sistema como outro
   * utilizador. Nas entidades em que o servidor deixa escrever
   * (`impersonator.canWrite`) não há bloqueio nenhum.
   */
  get isReadOnly(): boolean {
    let impersonator = this.userAuth?.impersonator;
    if (!this.userAuth) {
      try {
        impersonator = JSON.parse(localStorage.getItem("currentUser") || "{}")?.impersonator;
      } catch {
        return false;
      }
    }
    return !!impersonator && impersonator.canWrite !== true;
  }

  /** Em "Ver como" só as permissões de leitura são concedidas. */
  isAllowedWhileReadOnly(permission: string): boolean {
    return (
      !this.isReadOnly ||
      READ_ONLY_PERMISSION_PREFIXES.some((prefix) => permission?.startsWith(prefix))
    );
  }

  hasPermission(permission: string): boolean {
    if (!this.isAllowedWhileReadOnly(permission)) return false;
    // if (this.userAuth?.supportUser === true)
    //   return true
    if (!this.permissions || this.permissions.size === 0)
      this.loadPermissions([]);
    return (
      this.userAuth.role?.name === "MASTER" || this.permissions.has(permission)
    );
  }

  hasOneOfPermissionsList(permissions: string[]): boolean {
    return permissions.some(
      (p) => this.isAllowedWhileReadOnly(p) && this.permissions.has(p),
    );
  }

  /**
   * O perfil do utilizador alarga a leitura de `resource` a toda a entidade?
   * Vem do `dataScopes` do `user-auth`; sem ele é sempre `false`, e o
   * ecrã fica exatamente como era. Serve só para mostrar o que o servidor
   * já deixa ler — nunca substitui a verificação do servidor.
   */
  hasEntityDataScope(resource: string): boolean {
    let scopes = this.userAuth?.dataScopes;
    if (!scopes) {
      try {
        scopes = JSON.parse(localStorage.getItem("currentUser") || "{}")?.dataScopes;
      } catch {
        scopes = undefined;
      }
    }
    return Array.isArray(scopes) && scopes.includes(resource);
  }

  /**
   * A entidade deixa os diretores de loja (STORE OPERATOR) atuar como
   * consultores? Vem do `user-auth`; sem a chave é sempre `false` e os ecrãs
   * ficam exatamente como eram. O servidor nunca bloqueou o STORE OPERATOR
   * como angariador — isto só alinha o front com o interruptor do MASTER.
   */
  storeOperatorActsAsConsultant(): boolean {
    let flag = this.userAuth?.storeOperatorActsAsConsultant;
    if (flag === undefined) {
      try {
        flag = JSON.parse(localStorage.getItem("currentUser") || "{}")
          ?.storeOperatorActsAsConsultant;
      } catch {
        flag = undefined;
      }
    }
    return flag === true;
  }

  /**
   * Atualiza em memória (e no localStorage) flags da entidade recebidas por
   * outro pedido, para não esperar por um novo carregamento da app.
   */
  refreshEntityFlags(
    partial: Pick<User, "storeOperatorActsAsConsultant">,
  ): void {
    if (this.userAuth) {
      this.userAuth = { ...this.userAuth, ...partial };
    }
    try {
      const raw = localStorage.getItem("currentUser");
      if (raw) {
        localStorage.setItem(
          "currentUser",
          JSON.stringify({ ...JSON.parse(raw), ...partial }),
        );
      }
    } catch {
      // localStorage indisponível: fica só em memória
    }
  }

  /** Nome do papel de um agente, nas três formas que os ecrãs usam. */
  resolveAgentRoleName(agent: any): string {
    return `${agent?.role?.name ?? agent?.roleName ?? agent?.role ?? ""}`
      .trim()
      .toUpperCase();
  }

  isStoreOperatorAgent(agent: any): boolean {
    return this.resolveAgentRoleName(agent) === "STORE OPERATOR";
  }

  /**
   * Pode este agente ser angariador/vendedor? Um STORE OPERATOR só quando a
   * entidade ligou o interruptor; os restantes papéis sempre.
   */
  canBeListingAgent(agent: any): boolean {
    return (
      !this.isStoreOperatorAgent(agent) || this.storeOperatorActsAsConsultant()
    );
  }

  /** Consultor "de facto": AGENT/CD, ou STORE OPERATOR com o interruptor ligado. */
  actsAsConsultant(role: any = null): boolean {
    if (!role) role = this.userAuth?.role?.name;
    return (
      this.isAgent(role) ||
      (role === "STORE OPERATOR" && this.storeOperatorActsAsConsultant())
    );
  }

  isRealMaster(role: any = null) {
    if (!role) role = this.userAuth.role?.name;
    return role === "MASTER";
  }

  isMaster(role: any = null) {
    if (!role) role = this.userAuth.role?.name;
    return this.mastersRoles.includes(role);
  }

  isAgent(role: any) {
    return this.agentRoles.includes(role);
  }

  isNotAdmin(role: any) {
    return this.noAdminRoles.includes(role);
  }

  checkFullPermission(
    permission: string,
    storeId: any,
    mainUserId: any,
  ): boolean {
    if (!this.isAllowedWhileReadOnly(permission)) return false;
    if (this.userAuth?.role?.name === "MASTER") {
      return true;
    } else if (this.userAuth?.role?.name === "STORE OPERATOR") {
      if (
        this.userAuth.stores &&
        this.userAuth.stores.includes(storeId) &&
        this.hasPermission(permission)
      ) {
        return true;
      }
    } else if (this.userAuth?.role?.name === "COMMERCIAL DIRECTOR") {
      if (
        this.hasPermission(permission) &&
        (this.userAuth?.associatedUsers?.includes(mainUserId) ||
          this.userAuth?.id === mainUserId)
      ) {
        return true;
      }
    }
    return false;
  }

  canEditProperty(user: User, storeId: any, isNew = false, mainUserId?: any) {
    if (this.isReadOnly) return false;
    if (user?.role?.name === "MASTER") {
      return true;
    } else {
      if (this.hasPermission("EDIT_PROPERTIES")) {
        if (
          user?.role?.name === "STORE OPERATOR" &&
          ((user.stores && user.stores.includes(storeId)) || isNew)
        ) {
          return true;
        } else if (user?.role?.name === "COMMERCIAL DIRECTOR") {
          if (
            user?.associatedUsers?.includes(mainUserId) ||
            isNew ||
            user?.id === mainUserId
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }

  canDeleteProperty(user: User, storeId: any, isNew = false) {
    if (this.isReadOnly) return false;
    if (user?.role?.name === "MASTER") {
      return true;
    } else {
      if (this.hasPermission("DELETE_PROPERTIES")) {
        if (
          user?.role?.name === "STORE OPERATOR" &&
          user.stores &&
          user.stores.includes(storeId)
        ) {
          return true;
        }
      }
    }
    return false;
  }

  checkMasterPermission(permission: string, storeId: any, isNew = false) {
    if (!this.isAllowedWhileReadOnly(permission)) return false;
    if (this.userAuth?.role?.name === "MASTER") {
      return true;
    }

    if (
      this.userAuth?.role?.name === "STORE OPERATOR" &&
      ((this.userAuth.stores &&
        this.userAuth.stores.includes(storeId) &&
        this.hasPermission(permission)) ||
        isNew)
    ) {
      return true;
    }

    return false;
  }

  checkAgentPermission(id: any, permission: string) {
    if (!this.isAllowedWhileReadOnly(permission)) return false;
    if (
      (this.userAuth?.role?.name === "AGENT" &&
        this.userAuth?.id === id &&
        this.hasPermission(permission)) ||
      (this.userAuth?.role?.name === "COMMERCIAL DIRECTOR" &&
        (this.userAuth?.id === id ||
          this.userAuth?.associatedUsers?.includes(id)) &&
        this.hasPermission(permission))
    ) {
      return true;
    }
    return false;
  }

  checkMasterSomePermissions(
    permissions: string[],
    storeId: any,
    isNew = false,
  ) {
    if (!permissions.some((p) => this.isAllowedWhileReadOnly(p))) return false;
    if (this.userAuth?.role?.name === "MASTER") {
      return true;
    }
    if (
      this.userAuth?.role?.name === "STORE OPERATOR" &&
      ((this.userAuth.stores &&
        this.userAuth.stores.includes(storeId) &&
        this.hasOneOfPermissionsList(permissions)) ||
        isNew)
    ) {
      return true;
    }
    return false;
  }

  isTestSubscription(plan: string) {
    if (!plan) return true;

    return this.testPlans.includes(plan);
  }

  set user(user: User) {
    this.userAuth = user;
  }

  get currentRole(): string | undefined {
    return this.userAuth?.role?.name;
  }
}
